import React, { useState, useEffect } from 'react';
import FieldContainer from 'components/layout/FieldContainer';
import { getValidationSchema } from 'utils/validationProfExpSchemaGenerator';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styles from '../ProfBackground.module.scss';
import CollaborationItemEditing from './CollaborationItemEditing';
import ProfBackgroundItem from '../ProfBackgroundItem';
import { useMutation } from '@apollo/client';
import { DELETE_USER_COLLABORATION, SAVE_USER_COLLABORATION } from '../../../../api';
import { Alert, Snackbar } from '@mui/material';
import moment from 'moment';
import PartialLoader from 'components/layout/PartialLoader';

const COLLABORATIONS = 'Collaborations';
const EMPTY_TEXT = 'Add your collaboration';

const Notification = React.forwardRef((props, ref) => (
  <Alert elevation={6} ref={ref} variant="filled" {...props} />
));

const Collaboration = ({ userProps, isCurrentUser, setUserData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const validationSchema = getValidationSchema('collaborations');
  const formOptions = { resolver: yupResolver(validationSchema) };
  const formMethods = useForm(formOptions, { mode: 'all' });
  const [collaborations, setCollaborations] = useState([]);
  const [collaborationsList, setCollaborationsList] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveUserCollaboration] = useMutation(SAVE_USER_COLLABORATION);
  const [deleteUserCollaboration] = useMutation(DELETE_USER_COLLABORATION);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    setError,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'collaboration',
    keyName: 'key',
  });

  useEffect(() => {
    if (!isEditing) {
      setValue('collaboration', []);
    } else if (collaborationsList && collaborationsList.length) {
      collaborationsList.map((item) => {
        append({
          id: item.id,
          ps_education: item.ps_education,
          study: item.study,
          start_date: item.start_date,
          end_date: item.end_date,
          is_present: item.is_present,
        });
        return item;
      });
    }
  }, [isEditing]);

  useEffect(() => {
    if (userProps?.collaborations && userProps.collaborations.length) {
      setCollaborationsList(userProps.collaborations);
    } else {
      setCollaborationsList([]);
    }
  }, [userProps]);

  const handleAdd = () => {
    append({
      ps_education: '',
      study: '',
      start_date: null,
      end_date: null,
      is_present: true,
    });
  };

  const handleDelete = async (index) => {
    if (fields[index].id) {
      const res = await deleteUserCollaboration({ variables: { id: fields[index].id } });
      if (res.data) {
        remove(index);
        const newCollaborationsList = collaborationsList.filter((item, i) => index !== i);
        setUserData({ ...userProps, collaborations: newCollaborationsList });
        return true;
      }
    }
    remove(index);
    return true;
  };

  const handleNotificationClose = () => {
    setIsNotificationOpen(false);
  };

  const onFormSubmit = async (data) => {
    setLoading(true);
    const collaborationList = [];
    data.collaboration.map((item) => {
      const itemData = { ...item };
      itemData.start_date = moment(item.start_date).format('YYYY-MM-DD');
      if (itemData.end_date) {
        itemData.end_date = moment(item.end_date).format('YYYY-MM-DD');
      }
      collaborationList.push(itemData);
      return item;
    });
    const userCollaboration = await saveUserCollaboration({
      variables: { input: collaborationList },
    });
    if (userCollaboration?.data?.saveUserCollaboration?.collaborations) {
      setIsNotificationOpen(true);
      setIsEditing(false);
      setUserData({
        ...userProps,
        collaborations: userCollaboration.data.saveUserCollaboration.collaborations,
      });
    }
    setLoading(false);
  };

  return (
    <>
      {isCurrentUser ? (
        <FieldContainer
          key="collaboration"
          objKey="collaboration"
          title={COLLABORATIONS}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={handleAdd}
          formId="collaboration"
          containEditFieldButton
          isProfBackground
        >
          {collaborationsList.length === 0 && !isEditing && (
            <div className={styles.emptyText}>{EMPTY_TEXT}</div>
          )}
          {fields.length > 0 || (collaborationsList && collaborationsList.length) ? (
            <FormProvider {...formMethods}>
              <form id="collaboration" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <CollaborationItemEditing
                      key={exp.key}
                      formName="collaboration"
                      index={index}
                      profProps={exp}
                      formTitle="Collaborations"
                      data={collaborations}
                      setData={setCollaborations}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                      collaboration={exp.ps_education}
                      setError={setError}
                    />
                  ))}
                {!isEditing &&
                  collaborationsList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.study}
                      subTitle={exp.ps_education}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Collaborations Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : collaborationsList.length !== 0 ? (
        <FieldContainer
          key="collaboration"
          objKey="collaboration"
          title={COLLABORATIONS}
          className={styles.contentBox}
          isCurrentUser={isCurrentUser}
          setIsEditingProfile={setIsEditing}
          isEditingProfile={isEditing}
          addProf={handleAdd}
          formId="collaboration"
          containEditFieldButton
          isProfBackground
        >
          {fields.length > 0 || (collaborationsList && collaborationsList.length) ? (
            <FormProvider {...formMethods}>
              <form id="collaboration" onSubmit={handleSubmit(onFormSubmit)}>
                {isEditing &&
                  fields?.map((exp, index) => (
                    <CollaborationItemEditing
                      key={exp.key}
                      formName="collaboration"
                      index={index}
                      profProps={exp}
                      formTitle="Collaborations"
                      data={collaborations}
                      setData={setCollaborations}
                      control={control}
                      handleDelete={handleDelete}
                      setValue={setValue}
                      errors={errors}
                      isNew={!exp.id}
                    />
                  ))}
                {!isEditing &&
                  collaborationsList.map((exp, index) => (
                    <ProfBackgroundItem
                      key={exp.id}
                      index={index}
                      title={exp.study}
                      subTitle={exp.ps_education}
                      timeInterval={`${moment(exp.start_date).format('MM.YYYY')} - ${
                        exp.end_date ? moment(exp.end_date).format('MM.YYYY') : 'Present'
                      }`}
                    />
                  ))}
              </form>
            </FormProvider>
          ) : null}
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isNotificationOpen}
            onClose={handleNotificationClose}
            autoHideDuration={6000}
          >
            <Notification onClose={handleNotificationClose} severity="success">
              Collaborations Updated Successfully!
            </Notification>
          </Snackbar>
        </FieldContainer>
      ) : null}
      <PartialLoader loading={loading} isLightContainer />
    </>
  );
};

export default Collaboration;
